'use client';

import { useCallback, useSyncExternalStore } from 'react';
import { studioAIService } from '../services';

/**
 * Single source of truth for in-flight "Build a Report" runs.
 *
 * This module owns:
 *  1. A localStorage-backed list of active runs, so a refresh (or a
 *     jump to a non-chat page) can still surface the global toast.
 *  2. A single setInterval-driven polling loop that hits
 *     `/plan-mode/status/:runId` every `POLL_INTERVAL_MS` for every
 *     non-terminal entry.
 *
 * Previously this was layered on top of React Query's `useQueries` +
 * `refetchInterval`, but the observer lifecycle kept tangling with
 * our external store's re-render loop (every store update rebuilt the
 * queries options, which re-subscribed the observer and reset the
 * polling timer — so we'd see one successful poll then silence).
 * Polling is a singleton, tab-wide concern here, so a plain module
 * interval is the right abstraction.
 */

type PlannerStatusData = { status: string; reportId?: number; reportUrl?: string };

const STORAGE_KEY = 'studio-ai.activePlannerReports';
const PPTX_IDS_KEY = 'studio-ai.pptxReportIds';
const PPTX_CONV_IDS_KEY = 'studio-ai.pptxConversationIds';
const POLL_INTERVAL_MS = 20_000;
const ENTRY_TTL_MS = 24 * 60 * 60 * 1000;
const TERMINAL_STATUSES = new Set(['COMPLETE', 'FAILED']);

/** Persist a report ID as PPTX so history routing survives localStorage pruning. */
function markPptxReportId(reportId: number): void {
    if (typeof window === 'undefined') return;
    try {
        const raw = window.localStorage.getItem(PPTX_IDS_KEY);
        const ids: number[] = raw ? (JSON.parse(raw) as number[]) : [];
        if (!ids.includes(reportId)) {
            ids.push(reportId);
            window.localStorage.setItem(PPTX_IDS_KEY, JSON.stringify(ids));
        }
    } catch {}
}

/** Returns true when this report was generated as a PPTX slide deck. */
export function isPptxReport(reportId: number): boolean {
    if (typeof window === 'undefined') return false;
    try {
        const raw = window.localStorage.getItem(PPTX_IDS_KEY);
        if (!raw) return false;
        return (JSON.parse(raw) as number[]).includes(reportId);
    } catch {
        return false;
    }
}

/**
 * Mark a conversationId as belonging to a PPTX plan (detected via the
 * crosstab_retrieval_by_org step). Called from PlannerMessage when the step
 * is present in the message history so history routing works without a backend field.
 */
export function markPptxConversation(conversationId: string): void {
    if (typeof window === 'undefined' || !conversationId) return;
    try {
        const raw = window.localStorage.getItem(PPTX_CONV_IDS_KEY);
        const ids: string[] = raw ? (JSON.parse(raw) as string[]) : [];
        if (!ids.includes(conversationId)) {
            ids.push(conversationId);
            window.localStorage.setItem(PPTX_CONV_IDS_KEY, JSON.stringify(ids));
        }
    } catch {}
}

// ---------------------------------------------------------------------------
// Session-scoped crosstab data cache
// Keyed by conversationId. Written by planner-message when the crosstab
// step is confirmed; read by chat-messages when run_report fires via
// PlanSummaryCard (a different code path that doesn't go through
// PlannerMessage.handleSubmit).
// ---------------------------------------------------------------------------

const _crosstabSessionCache = new Map<string, string>();

export function setCrosstabSessionData(conversationId: string, data: string): void {
    _crosstabSessionCache.set(conversationId, data);
}

export function getCrosstabSessionData(conversationId: string): string | undefined {
    return _crosstabSessionCache.get(conversationId);
}

// ---------------------------------------------------------------------------
// Pending auto-fill cache for crosstab_retrieval_by_org step
// Keyed by conversationId. Written when a plan is started from the crosstab
// workspace; consumed by planner-message when the step first renders so it
// can auto-submit the crosstab without user interaction.
// ---------------------------------------------------------------------------

const _pendingAutoFillCache = new Map<string, string>();

export function setPendingAutoFillCrosstab(conversationId: string, context: string): void {
    _pendingAutoFillCache.set(conversationId, context);
}

export function getPendingAutoFillCrosstab(conversationId: string): string | undefined {
    return _pendingAutoFillCache.get(conversationId);
}

export function clearPendingAutoFillCrosstab(conversationId: string): void {
    _pendingAutoFillCache.delete(conversationId);
}

/** Returns true when this conversation produced a PPTX report (crosstab_retrieval_by_org step detected). */
export function isPptxConversation(conversationId: string): boolean {
    if (typeof window === 'undefined') return false;
    try {
        const raw = window.localStorage.getItem(PPTX_CONV_IDS_KEY);
        if (!raw) return false;
        return (JSON.parse(raw) as string[]).includes(conversationId);
    } catch {
        return false;
    }
}

export const PLANNER_STATUS_LABELS: Record<string, string> = {
    PLANNED: 'Getting your report ready...',
    INSIGHTS_READY: 'Finalizing your report...',
    RUNNING: 'Your report is being generated...',
    ANALYSES_COMPLETE: 'Analysis complete, finalizing your report...',
    COMPLETE: 'Report is ready!',
    FAILED: 'Report generation failed.',
    NOT_FOUND: 'Waiting for report to start...',
};

export interface ActivePlannerReportEntry {
    runId: string;
    conversationId: string;
    brandLabel?: string;
    startedAt: number;
    lastStatus?: string;
    lastReportId?: number;
    lastReportUrl?: string;
    /** Timestamp of the most recent successful poll. */
    lastPolledAt?: number;
    /** Number of consecutive failed polls since the last success. */
    consecutiveErrors?: number;
    /** Output format chosen by the user when kicking off this report. */
    outputFormat?: 'pdf' | 'pptx';
}

export interface ActivePlannerReportsApi {
    reports: ActivePlannerReportEntry[];
    upsert: (
        entry: Partial<ActivePlannerReportEntry> &
            Pick<ActivePlannerReportEntry, 'runId' | 'conversationId'>,
    ) => void;
    /**
     * Hide the toast for THIS browser tab only. Storage is left intact
     * so a refresh / new tab will see the run again until it terminates
     * or is explicitly cancelled. Polling continues in the background.
     */
    dismiss: (runId: string) => void;
    /**
     * Permanently remove the entry from localStorage (and stop tracking
     * polling). Used for the "Cancel" button and after the user clicks
     * "View" on a completed report — i.e. anywhere the user has
     * indicated they're done with this run for good.
     */
    cancel: (runId: string) => void;
    /**
     * Entry for a specific runId (or undefined if not tracked), whether or
     * not its toast was hidden in this tab. Use
     * this on chat pages to drive the composer's "report in progress"
     * state without spinning up a second polling observer.
     */
    getEntry: (runId: string | null | undefined) => ActivePlannerReportEntry | undefined;
}

// ---------------------------------------------------------------------------
// Persistent store
// ---------------------------------------------------------------------------

type Listener = () => void;

function safeReadStorage(): ActivePlannerReportEntry[] {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
            (e): e is ActivePlannerReportEntry =>
                !!e &&
                typeof e === 'object' &&
                typeof e.runId === 'string' &&
                typeof e.conversationId === 'string',
        );
    } catch {
        return [];
    }
}

function safeWriteStorage(entries: ActivePlannerReportEntry[]): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
        // Quota / serialization errors should not break the UI — the
        // in-memory copy still drives the toast during this session.
    }
}

function pruneExpired(entries: ActivePlannerReportEntry[]): ActivePlannerReportEntry[] {
    const now = Date.now();
    return entries.filter((e) => now - e.startedAt < ENTRY_TTL_MS);
}

const EMPTY: ActivePlannerReportEntry[] = Object.freeze(
    [],
) as unknown as ActivePlannerReportEntry[];

let storeState: ActivePlannerReportEntry[] = EMPTY;
let visibleState: ActivePlannerReportEntry[] = EMPTY;
let hydrated = false;
const listeners = new Set<Listener>();
let storageEventBound = false;

/**
 * Run IDs that the user has X-ed out for this tab. Intentionally NOT
 * persisted: the bug we're fixing is that "dismiss" used to wipe
 * storage but then re-appear because chat-page state would re-upsert
 * the same runId. Keeping this in module memory means a refresh
 * surfaces the toast again (so the user can still see in-flight runs
 * after a reload) while a same-session re-render stays hidden.
 */
const dismissedThisSession = new Set<string>();

function emit(): void {
    listeners.forEach((listener) => listener());
}

function recomputeVisibleState(): void {
    if (dismissedThisSession.size === 0) {
        visibleState = storeState;
        return;
    }
    const filtered = storeState.filter((entry) => !dismissedThisSession.has(entry.runId));
    // Preserve referential equality when nothing was filtered — useSyncExternalStore
    // bails out of re-renders only if the snapshot reference is stable.
    visibleState = filtered.length === storeState.length ? storeState : filtered;
}

function isTerminal(entry: ActivePlannerReportEntry): boolean {
    return !!entry.lastStatus && TERMINAL_STATUSES.has(entry.lastStatus);
}

function ensureHydrated(): void {
    if (hydrated || typeof window === 'undefined') return;
    storeState = pruneExpired(safeReadStorage());
    safeWriteStorage(storeState);
    // A run that had already finished before this page loaded was announced
    // in the tab that watched it finish. Showing it again on every refresh
    // brought back a "Report is ready" toast for each report built in the
    // last day. Hide them for this tab without touching storage, so another
    // open tab still showing one keeps it. A run still in flight at load
    // shows as before, and shows "ready" when this tab sees it finish.
    storeState.forEach((entry) => {
        if (isTerminal(entry)) dismissedThisSession.add(entry.runId);
    });
    recomputeVisibleState();
    hydrated = true;
}

function setStoreState(next: ActivePlannerReportEntry[], persist: boolean): void {
    storeState = next;
    if (persist) safeWriteStorage(storeState);
    recomputeVisibleState();
    emit();
    // Any state change can flip the "needs polling" answer — an entry
    // became terminal, a new run was added, the last run was dismissed.
    // Keep the interval in sync with whatever the store now looks like.
    ensurePolling();
}

function bindStorageEvent(): void {
    if (storageEventBound || typeof window === 'undefined') return;
    window.addEventListener('storage', (evt) => {
        if (evt.key !== STORAGE_KEY) return;
        // Cross-tab update — trust whatever landed in localStorage and
        // fan out to subscribers without re-persisting (avoid a loop).
        storeState = pruneExpired(safeReadStorage());
        recomputeVisibleState();
        ensurePolling();
        emit();
    });
    storageEventBound = true;
}

function subscribe(listener: Listener): () => void {
    ensureHydrated();
    bindStorageEvent();
    listeners.add(listener);
    ensurePolling();
    return () => {
        listeners.delete(listener);
        ensurePolling();
    };
}

function getSnapshot(): ActivePlannerReportEntry[] {
    return visibleState;
}

function getServerSnapshot(): ActivePlannerReportEntry[] {
    return EMPTY;
}

/** Every tracked run, hidden toasts included: a hidden toast still tracks its run. */
function getTrackedSnapshot(): ActivePlannerReportEntry[] {
    return storeState;
}

function upsertEntry(
    entry: Partial<ActivePlannerReportEntry> &
        Pick<ActivePlannerReportEntry, 'runId' | 'conversationId'>,
): void {
    ensureHydrated();
    // If the user already X-ed this run out for this tab, don't let a
    // re-render of a chat page silently re-surface it.
    if (dismissedThisSession.has(entry.runId)) return;
    const prev = storeState;
    const idx = prev.findIndex((e) => e.runId === entry.runId);
    if (idx === -1) {
        setStoreState(
            [
                ...prev,
                {
                    startedAt: Date.now(),
                    ...entry,
                } as ActivePlannerReportEntry,
            ],
            true,
        );
        // Fire the first status check immediately so the UI doesn't
        // have to wait a full interval to show progress.
        pollOnce(entry.runId);
        return;
    }
    const existing = prev[idx];
    const entryRecord = entry as unknown as Record<string, unknown>;
    const existingRecord = existing as unknown as Record<string, unknown>;
    let changed = false;
    for (const key of Object.keys(entryRecord)) {
        if (entryRecord[key] !== existingRecord[key]) {
            changed = true;
            break;
        }
    }
    if (!changed) return;
    const next = prev.slice();
    next[idx] = { ...existing, ...entry };
    setStoreState(next, true);
}

/**
 * Session-only hide. Storage is untouched; polling continues so the
 * pill can come back on a refresh if the run is still alive.
 */
function dismissEntryForSession(runId: string): void {
    ensureHydrated();
    if (dismissedThisSession.has(runId)) return;
    dismissedThisSession.add(runId);
    recomputeVisibleState();
    emit();
}

/**
 * Permanent removal — wipe from storage and stop polling this run.
 * Used by the explicit "Cancel" button and the "View" click on a
 * completed report (the user is done with it).
 */
function cancelEntry(runId: string): void {
    ensureHydrated();
    dismissedThisSession.add(runId);
    const prev = storeState;
    const next = prev.filter((e) => e.runId !== runId);
    if (next.length === prev.length) {
        // Storage is already clean but we still added to the session
        // set above; emit so any visible state recomputes.
        recomputeVisibleState();
        emit();
        ensurePolling();
        return;
    }
    setStoreState(next, true);
}

function applyPollSuccess(runId: string, data: PlannerStatusData): void {
    const prev = storeState;
    const idx = prev.findIndex((e) => e.runId === runId);
    if (idx === -1) return;
    const existing = prev[idx];
    const unchanged =
        data.status === existing.lastStatus &&
        data.reportId === existing.lastReportId &&
        data.reportUrl === existing.lastReportUrl &&
        (existing.consecutiveErrors ?? 0) === 0;
    // If nothing observable changed there's no point emitting — skip
    // the re-render entirely. The polling interval itself is what
    // proves the loop is alive, not a timestamp in the store.
    if (unchanged) return;
    const next = prev.slice();
    next[idx] = {
        ...existing,
        lastStatus: data.status,
        lastReportId: data.reportId,
        lastReportUrl: data.reportUrl,
        lastPolledAt: Date.now(),
        consecutiveErrors: 0,
    };
    // Persist PPTX report IDs so history routing works after the active entry is pruned.
    if (data.reportId && existing.outputFormat === 'pptx') {
        markPptxReportId(data.reportId);
    }
    setStoreState(next, true);
}

function applyPollFailure(runId: string): void {
    const prev = storeState;
    const idx = prev.findIndex((e) => e.runId === runId);
    if (idx === -1) return;
    const existing = prev[idx];
    const nextErrors = (existing.consecutiveErrors ?? 0) + 1;
    // Give the backend a little grace before marking FAILED — a single
    // network blip shouldn't kill the toast. After 5 consecutive
    // errors (~100s) we promote to FAILED so the UI stops spinning.
    const promoteToFailed = nextErrors >= 5 && existing.lastStatus !== 'FAILED';
    const next = prev.slice();
    next[idx] = {
        ...existing,
        consecutiveErrors: nextErrors,
        lastStatus: promoteToFailed ? 'FAILED' : existing.lastStatus,
    };
    setStoreState(next, true);
}

// ---------------------------------------------------------------------------
// Polling loop
// ---------------------------------------------------------------------------
//
// One interval per tab, regardless of how many components are mounted.
// Starts when we have listeners + at least one non-terminal entry,
// stops when either condition flips.

let pollTimer: ReturnType<typeof setInterval> | null = null;
const inFlight = new Set<string>();

function hasActiveWork(): boolean {
    return storeState.some((e) => !isTerminal(e));
}

function ensurePolling(): void {
    if (typeof window === 'undefined') return;
    const shouldRun = listeners.size > 0 && hasActiveWork();
    if (shouldRun && pollTimer === null) {
        pollTimer = setInterval(pollAll, POLL_INTERVAL_MS);
        // Kick off an immediate poll so the first status update lands
        // within a few hundred ms of mount / hydration instead of
        // forcing the user to stare at a spinner for a full interval.
        pollAll();
    } else if (!shouldRun && pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
    }
}

async function pollOnce(runId: string): Promise<void> {
    if (inFlight.has(runId)) return;
    inFlight.add(runId);
    try {
        const data = await studioAIService.getReportStatus(runId);
        applyPollSuccess(runId, data);
    } catch {
        applyPollFailure(runId);
    } finally {
        inFlight.delete(runId);
        ensurePolling();
    }
}

function pollAll(): void {
    storeState.forEach((entry) => {
        if (isTerminal(entry)) return;
        pollOnce(entry.runId);
    });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useActivePlannerReports(): ActivePlannerReportsApi {
    const reports = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    const tracked = useSyncExternalStore(subscribe, getTrackedSnapshot, getServerSnapshot);

    const upsert = useCallback<ActivePlannerReportsApi['upsert']>((entry) => {
        upsertEntry(entry);
    }, []);

    const dismiss = useCallback<ActivePlannerReportsApi['dismiss']>((runId) => {
        dismissEntryForSession(runId);
    }, []);

    const cancel = useCallback<ActivePlannerReportsApi['cancel']>((runId) => {
        cancelEntry(runId);
    }, []);

    const getEntry = useCallback<ActivePlannerReportsApi['getEntry']>(
        (runId) => {
            if (!runId) return undefined;
            return tracked.find((e) => e.runId === runId);
        },
        [tracked],
    );

    return { reports, upsert, dismiss, cancel, getEntry };
}
